import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const FIELDS = [
  ["hours_per_video", "Durée d'un montage à la main (h / vidéo)", "Sert au compteur « Heures économisées »."],
  ["anon_ttl_days", "Durée de vie d'une session anonyme (jours)", "Jamais reliée à un compte → effacée avec ses fichiers."],
  ["anon_ip_daily_sessions", "Sessions anonymes max par IP et par 24 h", ""],
  ["anon_ip_daily_transcriptions", "Détections de paroles max par IP et par 24 h (sans compte)", "Groq"],
  ["anon_ip_daily_analyze", "Analyses de son max par IP et par 24 h (sans compte)", ""],
  ["anon_ip_daily_pexels", "Recherches banque max par IP et par 24 h (sans compte)", "Pexels"],
  ["anon_ip_daily_links", "Imports par lien max par IP et par 24 h (sans compte)", ""],
  ["sub_scale_pct", "Taille des sous-titres (%)", "100 = taille d'origine du moteur ; 85 = réduits de 15 % (défaut v4). S'applique à l'aperçu, au feed et aux exports."],
];

export default function SettingsAdmin() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [{ data: d }, { data: u }] = await Promise.all([api.get("/admin/settings"), api.get("/admin/usage/summary")]);
      setData(d); setForm(d.settings); setUsage(u);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const vals = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data: d } = await api.put("/admin/settings", vals);
      setForm(d.settings); toast.success("Réglages enregistrés"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const purge = async () => {
    if (!window.confirm("Effacer maintenant les sessions anonymes expirées et leurs fichiers ?")) return;
    try { const { data: r } = await api.post("/admin/anonymous/purge"); toast.success(`${r.removed} session(s) effacée(s)`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!data) return <p className="font-osd text-sm text-muted-foreground animate-pulse">CHARGEMENT…</p>;
  return (
    <div className="space-y-8" data-testid="admin-settings">
      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-usage-tiles">
          {["today", "week", "month", "total"].map((k) => (
            <div key={k} className="bg-card border border-border p-5">
              <p className="font-osd text-[10px] tracking-[0.18em] text-muted-foreground mb-2">{{ today: "VIDÉOS AUJOURD'HUI", week: "VIDÉOS 7 JOURS", month: "VIDÉOS 30 JOURS", total: "VIDÉOS AU TOTAL" }[k]}</p>
              <p className="font-display text-2xl font-extrabold" data-testid={`usage-${k}`}>{usage[k].videos}</p>
              <p className="text-xs text-muted-foreground mt-1">{usage[k].hours} h économisées</p>
            </div>
          ))}
        </div>
      )}
      <section className="bg-card border border-border p-6 sm:p-8">
        <h2 className="font-display text-lg font-bold mb-5">Réglages</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {FIELDS.map(([k, label, hint]) => (
            <label key={k} className="block text-sm">
              <span className="font-osd text-[11px] tracking-[0.15em] text-muted-foreground block mb-1.5">{label.toUpperCase()}</span>
              <input type="number" min="0" step="any" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`setting-${k}`} className="w-full bg-background border border-border px-4 h-11 text-sm focus:border-[#fc1c46] focus:outline-none" />
              {hint && <span className="text-[11px] text-muted-foreground block mt-1">{hint}</span>}
            </label>
          ))}
        </div>
        <button onClick={save} disabled={busy} data-testid="settings-save" className="mt-6 bg-primary text-white font-bold px-6 h-11 hover:opacity-90 disabled:opacity-50">Enregistrer</button>
      </section>
      <section className="bg-card border border-border p-6 sm:p-8" data-testid="admin-anonymous-card">
        <h2 className="font-display text-lg font-bold mb-3">Sessions anonymes</h2>
        <div className="grid grid-cols-3 gap-4 text-sm mb-5">
          <div><p className="font-osd text-[10px] tracking-[0.18em] text-muted-foreground mb-1">ACTIVES</p><p className="font-display text-xl" data-testid="anon-active">{data.anonymous.active}</p></div>
          <div><p className="font-osd text-[10px] tracking-[0.18em] text-muted-foreground mb-1">RELIÉES À UN COMPTE</p><p className="font-display text-xl" data-testid="anon-linked">{data.anonymous.linked}</p></div>
          <div><p className="font-osd text-[10px] tracking-[0.18em] text-muted-foreground mb-1">À EFFACER (&gt; {data.anonymous.ttl_days} J)</p><p className="font-display text-xl" data-testid="anon-to-purge">{data.anonymous.to_purge}</p></div>
        </div>
        <button onClick={purge} data-testid="anon-purge-now" className="border border-border px-5 h-11 text-sm hover:border-primary hover:text-primary transition-colors">Effacer les sessions expirées maintenant</button>
      </section>
    </div>
  );
}
